import { Link } from 'react-router-dom';
import type { HelpTopicDef } from '../../help/types';
import { headingId } from '../../help/anchors';

function formatUpdated(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export interface TocEntry {
  id: string;
  label: string;
}

/** The "On this page" outline: every h2 the topic renders, in render order. */
export function tocFor(topic: HelpTopicDef): TocEntry[] {
  const entries: TocEntry[] = [];
  topic.recipes?.forEach((r) => entries.push({ id: headingId(r.task), label: r.task }));
  topic.sections?.forEach((s) => entries.push({ id: headingId(s.heading), label: s.heading }));
  if (topic.related && topic.related.length > 0) entries.push({ id: 'related', label: 'Related' });
  return entries;
}

/**
 * The single renderer for every help topic. Semantic structure is the point:
 * one h1, h2 per recipe/section (with anchor ids for the TOC and deep links),
 * ol for steps, dl for glossary — this is what a screen reader or an AI
 * assistant reading the page consumes.
 */
export function HelpTopicView({ topic }: { topic: HelpTopicDef }) {
  return (
    <article className="help-topic">
      <div className="eyebrow">{topic.group}</div>
      <h1 className="page-title">{topic.title}</h1>
      <p className="help-updated">Updated {formatUpdated(topic.updated)}</p>

      {topic.intro?.map((p, i) => (
        <p key={i} className="help-intro">
          {p}
        </p>
      ))}

      {topic.recipes?.map((r) => (
        <section key={r.task} className="help-recipe">
          <h2 id={headingId(r.task)}>{r.task}</h2>
          <ol>
            {r.steps.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ol>
          {r.note && <p className="help-note">{r.note}</p>}
        </section>
      ))}

      {topic.sections?.map((s) => (
        <section key={s.heading}>
          <h2 id={headingId(s.heading)}>{s.heading}</h2>
          {s.body.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </section>
      ))}

      {topic.glossary && (
        <dl className="help-glossary">
          {topic.glossary.map((g) => (
            <div key={g.term}>
              {/* Same id scheme as the h2s: help search deep-links to a term. */}
              <dt id={headingId(g.term)}>{g.term}</dt>
              <dd>{g.def}</dd>
            </div>
          ))}
        </dl>
      )}

      {topic.related && topic.related.length > 0 && (
        <nav className="help-related" aria-label="Related">
          <h2 id="related">Related</h2>
          <ul>
            {topic.related.map((l) => (
              <li key={l.to}>
                <Link to={l.to}>{l.label}</Link>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </article>
  );
}
